<template>
  <div>
    <div :class="['map-here', showMapMobile ? 'showMapBox' : '']" id="mapHere"></div>

    <div class="package-container" v-if="package != null">
      <div class="title">
        <span class="header">Package {{ packageID }}
          <a href="#" v-show="package.ada.serviceDog"><i class="fas fa-wheelchair"></i></a>
          <a href="#" v-show="package.ada.wheelChairLift"><i class="fas fa-dog"></i></a>
        </span>
        <span v-bind:class="'status ' + package.statusClass"> {{ package.status }}</span>
      </div>

      <!-- PACKAGE INFO -->
      <div class="label-value">
        <div class="label">{{ $t('dispatch.mapDetails.packagePickup') }}</div>
        <div class="value">{{ getAddress(package.pickup) }}</div>
      </div>
      <div class="label-value">
        <div class="label">{{ $t('dispatch.mapDetails.packageDropoff') }}</div>
        <div class="value">{{ getAddress(package.dropoff) }}</div>
      </div>

      <div class="row">
        <div class="small-6 columns">
          <div class="label-value">
            <div class="label">{{ $t('dispatch.mapDetails.pickupTime') }}</div>
            <div class="value">{{ package.pickupTime }}</div>
          </div>
        </div>

        <div class="small-6 columns">
          <div class="label-value">
            <div class="label">PASSENGER / LUGGAGE / STOPS</div>
            <div class="value">{{ package.pax }} / {{ package.luggage }} / {{ package.stops }}</div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="small-6 columns">
          <div class="label-value">
            <div class="label">{{ $t('dispatch.mapDetails.serviceType') }}</div>
            <div class="value service-type" :style="{backgroundColor: package.rsvps[0].serviceBg}"
              v-if="package.rsvps.length && package.rsvps[0].optinShared && package.rsvps[0].optinShared.isOptinShared">
              {{ package.rsvps[0].serviceLabel }}
            </div>
            <div :class="['value service-type', package.serviceClass]" v-else>{{ package.serviceLabel }}</div>
          </div>
        </div>
        <div class="small-6 columns">
          <div class="label-value">
            <div class="label">{{ $t('dispatch.mapDetails.status') }}</div>
            <div :class="['value status', package.statusClass]">{{ package.status }}</div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="small-6 columns">
          <div class="label-value">
            <div class="label">FARE AMOUNT</div>
            <div class="value">${{ (package.fare + package.airportFee + package.tranvelInsurance + package.driverPayment.gasFee).toFixed(2) }}</div>
          </div>
        </div>
        <div class="small-6 columns">
          <div class="label-value">
            <div class="label">DRIVER PAYMENT</div>
            <div class="value">${{ getDriverPayment(package.driverPayment).toFixed(2) }}</div>
            <a class="payment-breakdown-button" href="javascript:;"
              @click="showBreakdown(true)">See Payment Breakdown</a>
          </div>
        </div>
      </div>

      <div class="row" v-if="isCompleted()">
        <div class="small-6 columns">
          <div class="label-value">
            <div class="label">COMPLETED</div>
            <div class="value">{{ isCompleted() }}</div>
          </div>
        </div>
      </div>

      <!-- PACKAGE INFO end -->

      <!-- DRIVER INFO -->
      <div class="divider"></div>
      <div v-if="package.driver !== null">

        <div class="row">
          <div class="small-6 columns">
            <div class="label-value">
              <div class="label">{{ $t('dispatch.mapDetails.driverName') }}</div>
              <div class="value driver-name">
                <div class="name">
                  <strong>
                    {{ package.driver.firstName }} {{ package.driver.lastName }}
                  </strong>
                </div>
                <div class="prov-options">
                  <i class="fas fa-ellipsis-v fa-lg" @click="showOptions = !showOptions"></i>

                  <transition name="slide-down">
                    <div class="options" v-show="showOptions">
                      <div class="button cta success block call" @click="callDriver(package.driver)">
                        <i class="fa fa-phone"></i>{{ $t('provider.call') }}
                      </div>
                      <div class="button cta block send-sms" @click="sendSMS()">
                        <i class="fas fa-comment-dots"></i>{{ $t('provider.sms') }}
                      </div>
                      <div class="button cta warning block send-notif" @click="showNotif">
                        <i class="fas fa-bell"></i>{{ $t('provider.notify') }}
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>
          </div>
          <div class="small-6 columns">
            <div class="label-value">
              <div class="label">ID</div>
              <div class="value">
                <a v-bind:href="'/#/provider/' + package.driver.id" target="_blank">
                  {{ package.driver.driverNum }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="small-6 columns">
            <div class="label-value">
              <div class="label">{{ $t('dispatch.mapDetails.driverPhone') }}</div>
              <div class="value">
                <a v-bind:href="'tel:' + package.driver.phone">
                  {{ package.driver.phone }}
                </a>
              </div>
            </div>
          </div>
          <div class="small-6 columns">
            <div class="label-value">
              <div class="label">{{ $t('dispatch.mapDetails.driverEmail') }}</div>
              <div class="value">
                <a v-bind:href="'mailto:' + package.driver.email">
                  {{ package.driver.email }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <a class="button error cta small unassign" @click="showReleaseModal"
          v-if="['CANCELED', 'COMPLETED'].indexOf(package.status) === -1">Release Driver</a>
      </div>
      <div v-else>
        <div class="label-value">
          <div class="label">{{ $t('dispatch.mapDetails.driverInfo') }}</div>
          <div class="value">
            <div class="info">{{ $t('dispatch.mapDetails.packageDriverNone') }}</div>
          </div>
        </div>
      </div>
      <!-- DRIVER INFO end -->

      <!-- STOPS -->
      <div class="divider"></div>
      <div class="stops">
        <div class="title">STOPS FOR PACKAGE {{ package.id }}</div>
        <div v-bind:class="{ stop: true, pickup: leg.isPickup }" v-for="(leg, index) in package.legs">
          <div class="name">
            <i v-bind:class="{ 'fas fa-map-marker-alt fa-fw': true, pickup: leg.isPickup }"></i></span>
            <a :href="'/#/consumer/' + leg.customer.id" target="_blank">{{ leg.customer.firstName }} {{ leg.customer.lastName }}</a>
          </div>
          <div class="time">
            <span>{{leg.arrivalTime}}</span>
            <!-- <span v-if="leg.isPickup">{{ leg.pickupTime }}</span>
            <span v-else>TBD</span> -->
          </div>
          <div class="address">
            <span v-if="leg.isPickup">{{ getAddress(leg.pickup) }}</span>
            <span v-else>{{ getAddress(leg.dropoff) }}</span>
          </div>
          <div v-bind:class="'status ' + leg.statusClass">{{ leg.status }}</div>
          <div class="misc">
            <div class="rsvp-id">
              <span>ID:</span>
              <a :href="'/#/reservation/' + leg.id" target="_blank">{{ leg.id }}</a>
            </div>
            <div class="pax-luggage">
              <i class="fas fa-user fa-fw"></i><span>{{ leg.pax }}</span>
              <i class="fas fa-suitcase fa-fw"></i><span>{{ leg.luggage }}</span>
            </div>
          </div>
        </div>
        <div class="fixed-bottom">
          <transition name="slide-up">
            <div v-if="refreshPkg.success" class="refresh-success">Package Details updated!</div>
          </transition>
          <a :class="{'button cta':true, 'disabled':refreshLoading}" @click="refresh">
            <i :class="{'fas fa-sync-alt':true, 'fa-spin':refreshLoading}"></i>
            <span v-if='refreshLoading'>Loading</span>
            <span v-else>{{ $t('transactions.filter.refresh') }}</span>
          </a>
    </div>
      </div>
      <!-- STOPS end -->
    </div>

    <div class="toggle-mobile" @click="showMapMobile = !showMapMobile">
      <span v-if="!showMapMobile">Show Map <i class="fa fa-chevron-up"></i></span>
      <span v-else>Hide Map <i class="fa fa-chevron-down"></i></span>  
    </div>

    <!-- PAYMENT BREAKDOWN -->
    <div v-if="package !== null"
      v-bind:class="{ 'modal-container': true, show: showPaymentBreakdown }" @click="showBreakdown(false)">
      <div class="modal">
        <div class="modal-content">
          <div class="payment-breakdown" @click.stop>
            <a class="close" href="javascript:;" @click="showBreakdown(false)">
              <i class="far fa-times-circle fa-fw"></i>
            </a>
            <div class="title">PAYMENT BREAKDOWN</div>
            <div class="row breakdown">
              <div class="small-6 columns label">Fare Amount:</div>
              <div class="small-6 columns value">${{ package.driverPayment.base.toFixed(2) }}</div>
            </div>
            <div class="row breakdown">
              <div class="small-6 columns label">Fuel Surcharge:</div>
              <div class="small-6 columns value">${{ package.driverPayment.gasFee.toFixed(2) }}</div>
            </div>
            <div class="row breakdown">
              <div class="small-6 columns label">Bonus:</div>
              <div class="small-6 columns value">${{ package.driverPayment.bonus.toFixed(2) }}</div>
            </div>
            <div class="row breakdown">
              <div class="small-6 columns label">Tip:</div>
              <div class="small-6 columns value">${{ package.driverPayment.tip.toFixed(2) }}</div>
            </div>
            <div class="row breakdown">
              <div class="small-6 columns label">Pickup Fee:</div>
              <div class="small-6 columns value">${{ package.driverPayment.pickupFee.toFixed(2) }}</div>
            </div>
            <div class="row breakdown total">
              <div class="small-6 columns label">Total:</div>
              <div class="small-6 columns value">${{ getDriverPayment(package.driverPayment).toFixed(2) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- PAYMENT BREAKDOWN end -->

    <div v-bind:class="{ 'loading-modal-container': true, show: isLoading }">
      <div class="loading-modal">
        <div class="loading-modal-content">
          <i class="fas fa-circle-notch fa-spin fa-2x fa-fw"></i>
          <div class="message">Loading package {{ packageID }}...</div>
        </div>



      </div>
    </div>

    <div v-bind:class="{'release-modal': true, show: release.show}">
      <div class="release-modal-container" @click="release.show = false">
        <div class="release-modal-content" @click.stop>
          <div class="confirm-release" v-show="!release.processing">
            <div class="title">
              <span v-if="!release.loading">CONFIRM THE RELEASE</span>
              <span v-else-if="release.error.show">ERROR</span>
              <span v-else>Loading...</span>
            </div>
            <!-- <transition name="down-scale"> -->
            <div v-if="release.loading && !release.error.show" class="loading">
              <i class="fa fa-spinner fa-spin fa-fw fa-5x"></i>
              <div>Please wait...</div>
            </div>
            <!-- </transition> -->
            <transition name="slide-up">
              <div v-if="release.error.show" class="error">
                <i class="fas fa-exclamation-triangle fa-fw fa-5x"></i>
                <div>{{ release.error.message }}</div>
              </div>
            </transition>
            <transition name="slide-up">
              <div v-if="release.showContent && !release.error.show">
                <div class="message">
                  Are you sure you want to un-assign this package from
                  <a :href="'/#/provider/' + package.driver.id" target="_blank">{{ package.driver.firstName }} {{ package.driver.lastName }}</a>?
                </div>
                <div class="fee" v-if="release.fee > 0">
                  <p>The driver will have to pay an unassign fee of <b>${{ release.fee }}</b>. Do you want to charge the unassign fee?</p>
                  <label>
                    <input type="radio" name="release" value="1" v-model="release.charge"> Yes
                  </label>
                  <label>
                    <input type="radio" name="release" value="0" v-model="release.charge"> No
                  </label>
                  <div class="clear"></div>
                </div>
                <div class="sub-message">
                  If you are un-assigning the driver in order to edit this package, make sure that you <strong>assign it back to the same driver</strong>. Otherwise, make sure that you <strong>find another driver to cover this package</strong> or
                  <strong>push it back again to the pool</strong>.
                </div>
                <div class="row controls">
                  <div class="small-6 columns padding-R">
                    <a href="javascript:;" @click="release.show = false" class="button cta block small">NO</a>
                  </div>
                  <div class="small-6 columns padding-L">
                    <a href="javascript:;" class="button cta block small warning" @click="releaseDriver">YES</a>
                  </div>
                </div>
              </div>
            </transition>
          </div>
          <div class="releasing" v-show="release.processing">
            <i v-bind:class="{
              'fas fa-circle-notch fa-spin': release.loading,
              'fas fa-exclamation-triangle': !release.success && !release.loading,
              'fas fa-check-circle': release.success,
              'fa-fw fa-2x': true,
              success: release.success
            }"></i>
            <div class="message" v-show="release.loading">Un-assigning driver from this reservation...</div>
            <div class="message" v-show="release.success">Successfully un-assigned driver from the reservation.</div>
            <div class="message" v-show="release.error.show">{{ release.error.message }}</div>
            <div class="message-sub" v-show="release.error.show">Please Try again.</div>
            <a href="javascript:;" v-show="!release.loading" class="button cta block small warning" @click="closeRelease">Close</a>
          </div>
        </div>
      </div>
    </div>

    <div v-bind:class="{'release-modal': true, show: refreshPkg.show}">
      <div class="release-modal-container" @click="refreshPkg.show = false">
        <div class="release-modal-content" @click.stop>
          <div class="confirm-release">
          <transition name="slide-up">
              <div v-if="refreshPkg.error" class="error">
                <i class="fas fa-exclamation-triangle fa-fw fa-5x"></i>
                <div>{{ refreshPkg.message }}</div>
              </div>
            </transition>
              <div class="message-sub">Please try again.</div>
              <div class="releasing">
                <a href="javascript:;" class="button cta block small warning" @click="closeRelease">Close</a>
              </div>
          </div>
        </div>
      </div>
    </div>

    <notification-modal  ref="notif" 
    :isList="false" :provId="package!== null && package.driver ? package.driver.id : null"
    :removeAllActiveUserOption="true" :hideTargetOption="true"></notification-modal>
    <sms-modal ref="sms"
    :isList="false" :provId="package!== null && package.driver ? package.driver.id : null"
    :removeAllActiveUserOption="true" :hideTargetOption="true"></sms-modal>
  </div>
</template>

<script>
/* global CONFIG */
const request = require('browser-request')
import mapSettings from '@/values/map-settings'
import mapPins from '@/values/map-pin'
import ApiPackage from '@/modules/api/package'
import driverRelease from '@/modules/api/driver-release'
import { setTimeout } from 'timers';
import notificationModal from '../App/NotificationModal'
import smsModal from '../App/SMSModal'

export default {
  name: 'package-details',
  components: { notificationModal, smsModal },
  data () {
    return {
       hereMaps: {
        map: {},
        platform: {},
        router: {},
        geocoder: {},
        directions: [],
        ui: {}
      },
      mapOptions: mapSettings,
      pickupPin: mapPins.pickup,
      dropoffPin: mapPins.dropoff,
      center: { lat: 33.942791, lng: -118.410042 }, // LAX
      isLoading: false,
      packageRoute: [],
      showPaymentBreakdown: false,
      release: {
        show: false,
        loading: false,
        processing: false,
        showContent: false,
        fee: 0,
        charge: 1,
        success: false,
        error: {
          show: false,
          message: ''
        }
      },
      refreshLoading: false,
      refreshPkg: {
        show: false,
        message: '',
        error: false,
        loading: false,
        success: false
      },
      driverLocation: {
        lat: '',
        lng: ''
      },
      driverDestination: {
        lat: '',
        lng: ''
      },
      trackDriverDelay: '',
      showOptions: false,
      showOptionsDelay: '',
      showMapMobile: false
    }
  },
  computed: {
    packageID () { return this.$route.params.id },
    package () { return this.$store.state.packageDetails.package }
  },
  methods: {
    loadPackage (showLoading) {
      let self = this
      if (showLoading) {
        self.isLoading = true
      }

      ApiPackage.getPackage(self, self.packageID, r => {
        self.isLoading = false
        self.refreshLoading = false
        if (r.error) {
          self.$router.push({name: 'MissingPage'})
          return
        }

        self.$store.commit('packageDetails/setPackage', r)
        if (!showLoading) {
          self.refreshPkg.success = true
          setTimeout(() => {
            self.refreshPkg.success = false
          }, 3000)
        }
      })
    },
    routePackage () {
      let self = this

      let waypoints = self.package.route.slice(0, self.package.route.length)
      let routes = []
      waypoints.forEach(location => {
        // Pickup/Dropoff icon
        let marker = '<svg height="25" width="25" style="overflow: initial" viewBox="0 0 50 50">' +
          '<path d="{D}" fill="{FILL}"stroke="{STROKE}"' +
          'stroke-width="{STROKE_WIDTH}" /></svg>'

        let pickupDropoff = location.isPickup ? 'pickup' : 'dropoff'

        marker = marker.replace('{D}', mapPins[pickupDropoff].path)
          .replace('{STROKE}', mapPins[pickupDropoff].strokeColor)
          .replace('{STROKE_WIDTH}', mapPins[pickupDropoff].strokeWeight)
          // .replace('{FILL}', mapPins[pickupDropoff].fillColor)
        if (!location.isPickup && location.status === 'complete') {
          marker = marker.replace('{FILL}', mapPins.pickup.fillColor)
        } else {
          marker = marker.replace('{FILL}', mapPins[pickupDropoff].fillColor)
        }
        let icon = new window.H.map.DomIcon(marker)
        let coords = {lat: location.lat, lng: location.lng}
        let pin = new window.H.map.DomMarker(coords, {icon: icon})

        // if (!location.isPickup) {
          let content = `<div class="here-info-window-content package-detail">
            <label>RSVP ID:</label>
            <a class="value" href="/#/reservation/{RSVPID}" target="_blank">{RSVPID}</a>
            <label>GUEST NAME:</label>
            <span class="value">{GUEST}</span>
            <label>LOCATION:</label>
            <span class="value">{LOCATION}</span>
          </div>`
          content = content.replace(/{RSVPID}/gi, location.rsvpID)
            .replace('{GUEST}', location.guestName)
            .replace('{LOCATION}', `${location.address2} ${location.address1}, ${location.city}`)
          pin.id = 'marker'
          pin.lat = location.lat
          pin.lng = location.lng
          let bubble = new window.H.ui.InfoBubble(coords, {content: content})
          bubble.addClass('package-detail-iw')
          bubble.close()
          self.hereMaps.ui.addBubble(bubble)
          pin.addEventListener('tap', function (evt) {
            bubble.open()
          }, false);
        // }

        self.hereMaps.map.addObject(pin)

        routes.push(coords)
      })
      self.setRoute(routes, false)
      self.trackDriver()
    },
    setRoute (routes, isDriverTrack) {
      let self = this

      let url = 'https://router.hereapi.com/v8/routes?apiKey={API_KEY}&transportMode=car&return=polyline,summary'
      url = url.replace('{API_KEY}', CONFIG.hereApi.key)
      routes.forEach((coords, index) => {
        if (index == 0) {
          url += `&origin=${coords.lat},${coords.lng}`
        } else if (index == routes.length - 1) {
          url += `&destination=${coords.lat},${coords.lng}`
        } else {
          url += `&via=${coords.lat},${coords.lng}`
        }
      })

      request.get({
        url: url,
        json: true
      }, function (err, res, body) {
        if (err) {
          self.onCalculateError(err)
          return
        }
        self.onCalculateSuccess(isDriverTrack, body)
      })
    },
    onCalculateSuccess (isDriverTrack, result) {
      if (!result.routes.length) return
      var route = result.routes[0].sections
      if (isDriverTrack) {
        this.setTrackDriverPin(route)
        return
      }
      this.addRouteShapeToMap(route, isDriverTrack)
    },
    onCalculateError (data) {
      console.error('ROUTING ERROR', data)
    },
    addRouteShapeToMap (routes, isDriverTrack) {
      let self = this
      routes.forEach((route, index) => {
        let linestring = window.H.geo.LineString.fromFlexiblePolyline(route.polyline);
        let polyline = new window.H.map.Polyline(linestring, {
          style: {
            lineWidth: 5,
            strokeColor: isDriverTrack ? 'rgba(13, 121, 242, 0.6)' : 'rgba(25, 25, 25, 0.7)'
          }
        });
        polyline.id = `rsvpRoute${index}`
        self.hereMaps.map.addObject(polyline)
      })
      self.setCenter()
    },
    setCenter () {
      let self = this
      let group = new window.H.map.Group()

      for (let object of self.hereMaps.map.getObjects()) {
        if (object.id === 'groupBound') {
          self.hereMaps.map.removeObject(object)
        }
      }

      let groupObject = []
      for (let object of self.hereMaps.map.getObjects()) {
        if (object.id.indexOf('marker') > -1) {
          groupObject.push(new window.H.map.Circle(
            new window.H.geo.Point(object.lat, object.lng),
            5000,
            { style: { fillColor: 'rgba(0, 0, 0, 0)', strokeColor: 'rgba(0, 0, 0, 0)' } }
          ))
        }
      }

      if (groupObject.length) {
        group.addObjects(groupObject)
        group.id = 'groupBound'
        self.hereMaps.map.addObject(group)
  
        self.hereMaps.map.getViewModel().setLookAtData({
          bounds: group.getBoundingBox()
        })
      }
    },
    getAddress (location) {
      if (location.geofenceType === 'airport' &&
          location.geofenceName !== '') {
        let result = location.geofenceName
        if (location.terminal && location.terminal !== '' && location.terminal !== 'undefined') {
          result += ' - Terminal ' + location.terminal
        }
        return result
      }
      return location.title

      // let address = ''
      // if (location.address2 && location.address2 !== '' && location.address2 !== 'undefined') {
      //   address = address + location.address2 + ' '
      // }
      // if (location.address1) {
      //   address = address + location.address1 + ', '
      // }
      // if (location.city) {
      //   address = address + location.city + ' '
      // }
      // if (location.state) {
      //   address = address + location.state + ' '
      // }
      // if (location.zip) {
      //   address = address + location.zip
      // }

      // return address
    },
    getDriverPayment (breakdown) {
      return breakdown.base + breakdown.bonus + breakdown.tip + breakdown.pickupFee + breakdown.gasFee
    },
    showBreakdown (value) { this.showPaymentBreakdown = value },
    showReleaseModal () {
      let self = this
      self.release.show = true
      self.release.processing = false
      self.release.success = false

      if (self.release.loading) { return }

      self.release.loading = true
      self.release.showContent = false
      self.release.error.show = false
      self.release.charge = 1

      driverRelease.getReleaseFee(self, {
        id: self.packageID,
        type: 'package'
      }, function (r) {
        self.release.loading = false

        if (r.error) {
          self.release.error.show = true
          self.release.error.message = r.error.message
          return
        }

        self.release.fee = parseFloat(r.Charge)
        if (self.release.fee === 0) {
          self.release.charge = 0
        }
        self.release.showContent = true
      })
    },
    releaseDriver () {
      let self = this
      self.release.processing = true
      self.release.success = false
      self.release.loading = true

      self.$sdk.delete('/admin/package/' + self.packageID + '/driver', {
        token: self.$store.state.session.sessionToken,
        charge: self.release.charge
      }, r => {
        self.release.loading = false
        if (r.error) {
          console.log(r)
          self.release.error.show = true
          self.release.error.message = r.error.message
          return
        }
        self.release.success = true
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      })
    },
    closeRelease () {
      this.release.show = false
      if (!this.release.error.show) {
        window.location.reload()
      }
    },
    refreshTransaction () {
      let self = this
      self.$sdk.post('/admin/package/' + self.packageID + '/refresh', {
        token: self.$store.state.session.sessionToken
      }, r => {
        if (r.error){
          self.refreshPkg.message = r.error.message
          self.refreshPkg.error = true
          self.refreshPkg.show = true
        } else {
          self.loadPackage(false)
        }
      })
    },
    refresh () {
      this.refreshLoading = true
      this.refreshTransaction()
    },
    isCompleted () {
      let lastId = this.package.route[this.package.route.length - 1].rsvpID
      let lastStop = this.package.rsvps.find(x => x.id === lastId)
      return lastStop.completed
    },
    trackDriver () {
      let self = this

      let stops = []
      self.package.route.forEach(route => {
        if (!route.isPickup) {
          stops.push(route.rsvpID)
        }
      })

      let rsvp = ''
      stops.every((stop, index) => {
        rsvp = self.package.rsvps.find(x => x.id === stop && ['ontheway', 'arrived', 'onboard'].indexOf(x.rsvpStatus) > -1)
        return !rsvp ? true : false
      })

      if (self.trackDriverDelay) {
        clearTimeout(self.trackDriverDelay)
      }

      if (rsvp) {
        self.$sdk.get(`/provider/${rsvp.driver.id}/tracks`, {token: self.$store.state.session.sessionToken}, res => {
          if (!res.error && res.list.length) {
            self.driverLocation = {
              lat: res.list[0].lat,
              lng: res.list[0].lng
            }
            self.driverDestination = {
              lat: rsvp.dropoff.lat,
              lng: rsvp.dropoff.lng
            }
            self.setRoute([self.driverLocation, self.driverDestination], true)
          }
        })
      } else {
        self.trackRemoveDriver()
      }
    },
    computeHeading (orig, dest) {
      let y = Math.sin(dest.lng - orig.lng) * Math.cos(dest.lat)
      let x = Math.cos(orig.lat) * Math.sin(dest.lat) -
              Math.sin(orig.lat) * Math.cos(dest.lat) * Math.cos(dest.lng - orig.lng)

      let brng = Math.atan2(y, x) * 180 / Math.PI
      return Math.round(360 - ((brng + 360) % 360))
    },
    setTrackDriverPin (data) {
      let self = this
      self.trackRemoveDriver()

      // let marker = `<div class="rotator">
      //   <div class='here-marker car-icon' style="transform: rotate({HEADING}) translate(50%, 22px)"></div>
      // </div>`
      let marker = `
        <svg height="25" width="25" style="overflow: overlay; top: -12px; left: -12px;" viewBox="0 0 50 50">
          <path d="{D}" fill="{FILL}"stroke="{STROKE}" stroke-width="{STROKE_WIDTH}" />
        </svg>`
      marker = marker.replace('{D}', mapPins.car.path)
        .replace('{FILL}', mapPins.car.fillColor)
        .replace('{STROKE}', mapPins.car.strokeColor)
        .replace('{STROKE_WIDTH}', mapPins.car.strokeWeight)

      let heading = self.computeHeading(self.driverLocation, self.driverDestination)
      marker = marker.replace('{HEADING}', heading + 'deg')
      let icon = new window.H.map.DomIcon(marker)
      let pin = new window.H.map.DomMarker(self.driverLocation, {icon: icon})
      pin.id = 'driverPin'
      pin.lat = self.driverLocation.lat
      pin.lng = self.driverLocation.lng

      // let content = `<div class="here-info-window-content package-detail car">
      //   <label>ETA to next dropoff</label>
      //   <span class="value">{ETA}</span>
      // </div>`
      let content = `
        <div class="info-container">
          <div class="driver-info"><img src="{DRIVER_PHOTO}"/><a href="{DRIVER_URL}" target="_blank">{DRIVER_NAME}</a></div>
          <div class="vehicle-info"><img src="{VEHICLE_PHOTO}"/><strong>{VEHICLE_NAME}</strong><span>{VEHICLE_NUMBER}</span></div>
          <div class="eta-to-dropoff"><label>ETA to next dropoff</label><span class="value">{ETA}</span></div>
          <a id="DriverCall_{DRIVER_ID}" class="call-button button cta xsmall success block" href="javascript:;" @click="testMethod">CALL {DRIVER_FNAME}</a>
        </div>
      `
      let driver = self.package.driver
      content = content.replace('{DRIVER_PHOTO}', driver.photoUrl)
        .replace('{DRIVER_URL}', '/#/provider/' + driver.id)
        .replace('{DRIVER_ID}', driver.id)
        .replace('{DRIVER_NAME}', driver.firstName + ' ' + driver.lastName)
        .replace('{VEHICLE_PHOTO}', driver.vehicle.photoUrl)
        .replace('{VEHICLE_NAME}', driver.vehicle.name)
        .replace('{VEHICLE_NUMBER}', driver.vehicle.licensePlate)
        .replace('{DRIVER_FNAME}', driver.firstName)
      let travelTime = 0
      data.forEach(dat => {
        travelTime += dat.travelSummary.duration
      })
      if (travelTime > 60) {
        content = content.replace('{ETA}', Math.ceil(travelTime / 60) + ' minute(s)')
      } else {
        content = content.replace('{ETA}', travelTime + ' second(s)')
      }
      let bubble = new window.H.ui.InfoBubble(self.driverLocation, {content: content})
      bubble.addClass('H_driver-info')
      bubble.id = 'driverBubble'
      bubble.open()
      self.hereMaps.ui.addBubble(bubble)
      pin.addEventListener('tap', function (evt) {
        bubble.open()
      }, false)
      self.hereMaps.map.addObject(pin)

      setTimeout(() => {
        let elem = document.getElementById('DriverCall_' + driver.id)
        elem.addEventListener('click', evt => {
          self.callDriver(driver)
        })
      }, 500)

      self.trackDriverDelay = setTimeout(() => {
        self.trackDriver()
      }, 15000)
    },
    trackRemoveDriver () {
      let self = this
      for (let object of self.hereMaps.map.getObjects()) {
        if (object.id === 'driverPin') {
          self.hereMaps.map.removeObject(object)
        }
      }
      self.hereMaps.ui.getBubbles().forEach(bub => {
        if (bub.id === 'driverBubble') {
          self.hereMaps.ui.removeBubble(bub)
        }
      })
    },
    callDriver (driver) {
      let self = this
      self.$plugins.phone.startCall(self, {
        phone: driver.phone,
        name: driver.firstName + ' ' + driver.lastName
      })
    },
    call (user) {
      let self = this
      self.showOptions = false
      self.$plugins.phone.startCall(self, {
        phone: user.mobile,
        name: user.firstName + ' ' + user.lastName
      })
    },
    hideOptions () {
      let self = this
      self.showOptionsDelay = setTimeout(function () {
        self.showOptions = false
      }, 3500)
    },
    clearOptionDelay () {
      let self = this
      if (self.showOptionsDelay) { clearTimeout(self.showOptionsDelay) }
    },
    sendSMS () {
      this.showOptions = false
      if (this.$refs.sms.showSMSForm()) {
        this.$refs.sms.showSMSForm()
      }
    },
    showNotif () {
      this.showOptions = false
      if (this.$refs.notif.showNotifForm()) {
        this.$refs.notif.showNotifForm()
      }
    },
  },
  watch: {
    package (value) {
      if (value === null) {
        return
      }
      if (value.route === null) {
        return
      }
      this.routePackage()
    },
    packageID (value) {
      let self = this
      if (self.trackDriverDelay) {
        clearTimeout(self.trackDriverDelay)
      }
      for (let object of self.hereMaps.map.getObjects()) {
        self.hereMaps.map.removeObject(object)
      }
      self.hereMaps.ui.getBubbles().forEach(bub => {
        self.hereMaps.ui.removeBubble(bub)
      })
      self.loadPackage(true)
    },
    showOptions (value) {
      let self = this
      if (value) {
        self.hideOptions()
      }
    }
  },
  mounted () {
    let self = this
    let mapContainer = document.getElementById('mapHere')
    self.hereMaps.platform = new window.H.service.Platform({apikey: CONFIG.hereApi.key})

    let defaultLayers = self.hereMaps.platform.createDefaultLayers()
    self.hereMaps.map = new window.H.Map(mapContainer,
      defaultLayers.vector.normal.map, {
        center: self.center,
        zoom: 13,
        pixelRatio: window.devicePixelRatio || 1
      }
    )
    /* eslint-disable no-unused-vars */
    let behavior = new window.H.mapevents.Behavior(new window.H.mapevents.MapEvents(self.hereMaps.map))
    /* eslint-enable no-unused-vars */
    self.hereMaps.ui = window.H.ui.UI.createDefault(self.hereMaps.map, defaultLayers)

    self.loadPackage(true)
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/_status';
@import '../../assets/scss/_service-types';
$package-width: 500px;
$left-panel-width: 500px;
$fixed-bottom-height: 60px;
.fixed-bottom{ transition: all .6s ease; }

.toggle-mobile {
  display: none;
}

.package-container {
  position: fixed;
  top: 0;
  left: 0;
  width: $package-width;
  height: 100%;
  box-shadow: 2px 0 4px rgba(0,0,0,0.35);
  padding: 0 20px 20px;
  overflow: auto;
  & > .title {
    width: 100%;
    padding-left: 45px;
    height: 65px;
    margin-bottom: 15px;

    & .header {
      font-size: 20px;
      font-weight: 700;
      color: #3F2975;
      line-height: 40px;
      margin-right: 10px;
      display: block;

    }
    .status {
      margin-top: -12px;
    }

    & span {
      display: inline-block;
      vertical-align: middle;
    }
  }
}

.package-map{
  position: fixed;
  top: 0;
  left: $package-width;
  height: 100%;
  width: calc(100% - #{$package-width});
  display: none;
}

.map-here{
  position: fixed;
  top: 0;
  left: $package-width;
  height: 100%;
  width: calc(100% - #{$package-width});
}

.label-value{
  margin-top: 10px;
  & .label{
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 0;
    color: #8DA0A9;
  }

  & .value{
    color: #333;
    word-wrap: break-word;
    
    &.driver-name { display: flex; }
    &.service-type{ color: #fff; }
    &.status{ color: #fff; font-size: 16px; }

    & .info {
      font-weight: 700;
      margin-top: 5px;
      margin-bottom: 10px;
      font-size: 14px;
    }
  }
}

a{
  font-weight: 700;
}

.divider{
  display: block;
  width: 350px;
  margin: 20px auto;
  border-top: 1px solid rgba(0,0,0,0.2);
}

.stops {
  padding-bottom: 50px;
  & .stop {
    padding: 10px;
    border: 1px solid rgba(0,0,0,0.2);
    background-color: #fff;
    margin-bottom: 5px;
    border-radius: 3px;
    border-top: 3px solid #CA3538;
    position: relative;

    &.pickup{ border-top: 3px solid #2EE47D; }
  }
  & .title {
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 30px;
    color: #8DA0A9;
  }

  & .name {
    font-size: 14px;
    margin-bottom: 3px;
    & i {
      display: inline-block;
      margin-right: 2px;
      color: #CA3538;

      &.pickup{ color: #2EE47D; }
    }
  }

  & .time{
    font-size: 12px;
    font-weight: 700;
    color: #555;
  }

  & .address{
    margin-top: 5px;
    font-size: 16px;
    font-weight: 700;
    color: #555;
  }

  & .misc {
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 14px;
    color: #555;
    text-align: right;
    & .rsvp-id{  }

    & .pax-luggage {
      color: #777;
      font-size: 12px;
      font-weight: 700;
      & * {
        display: inline-block;
        vertical-align: middle;
      }
      & i{
        margin-left: 5px;
        display: inline-block;
      }

      & span{ margin-left: 2px; }
    }
  }
}

a.payment-breakdown-button {
  font-size: 10px;
  display: block;
}

.modal-container {
  position: fixed;
  top: -100%; left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  background-color: rgba(0,0,0,0.4);
  opacity: 0;
  transition: all 250ms;

  &.show {
    opacity: 1;
    top: 0;
  }

  & .modal {
    display: table;
    width: 100%;
    height: 100%;
  }

  & .modal-content {
    display: table-cell;
    vertical-align: middle;
  }
}

.payment-breakdown {
  width: 300px;
  background-color: #fff;
  margin: 0 auto;
  border-radius: 3px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  overflow: hidden;
  position: relative;

  & .close {
    display: block;
    position: absolute;
    color: #fff;
    top: 0;
    right: 0;
    padding: 10px 10px;
  }

  & .title {
    background-color: #333;
    color: #fff;
    font-weight: 700;
    text-align: center;
    padding: 10px 5px;
  }

  & .breakdown {
    padding: 5px 15px;
    font-size: 14px;
    text-transform: uppercase;

    &.total{
      font-size: 16px;
      padding: 10px 0 10px;
      margin: 0 15px;
      border-top: 1px solid rgba(0,0,0,0.15);
    }
  }
  & .label {
    text-align: left;
    color: #555;
  }
  & .value {
    text-align: right;
    font-weight: 700;
  }
}

.loading-modal-container {
  position: fixed;
  z-index: 999;
  top: -100%;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.3);
  display: table;
  opacity: 0;
  transition: all 250ms;

  &.show{
    top: 0;
    opacity: 1;
  }
}

.loading-modal {
  display: table-cell;
  vertical-align: middle;
}

.loading-modal-content {
  background-color: #fff;
  border-radius: 3px;
  max-width: 250px;
  margin: 0 auto;
  box-shadow: 0 2px 8px rgba(0,0,0,0.35);
  text-align: center;
  overflow: hidden;
  padding: 15px;

  & i { color: #0D79F2; }
  & .message {
    font-weight: 700;
    color: #777;
    margin-top: 5px;
  }
  .message-sub{
    padding: 0 15px;
    margin-bottom: 15px;
  }
}

.release-modal {
  position: fixed;
  z-index: 999;
  top: -100%;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.3);
  display: table;
  opacity: 0;
  transition: all 250ms;

  &.show{
    top: 0;
    opacity: 1;
  }
  .release-modal-container {
    display: table-cell;
    vertical-align: middle;
  }
}

.unassign {
  margin-top: 25px;
}

.release-modal-content {
  background-color: #fff;
  border-radius: 3px;
  max-width: 400px;
  margin: 0 auto;
  box-shadow: 0 2px 8px rgba(0,0,0,0.35);
  text-align: center;
  overflow: hidden;

  & .title {
    font-weight: 700;
    background-color: #333;
    color: #fff;
    padding: 10px;
  }

  .loading, .error{
    text-align: center;
    padding: 25px 0;
  }
  .loading{
    i{ color: #4fa1f7; }
    div{ color: #999; }
  }

  .error{
    i{ color: #f3e26d; }
    div{
      color: #777;
      margin-top: 15px;
    }
  }

  .fee{
    padding: 10px;
    margin: 10px;
    background: #f3f3f3;
    border-radius: 10px;
    color: #656565;
    border: 1px solid #808080;
    box-shadow: 1px 1px 3px rgba(0,0,0, .1);

    p{
      margin: 0 0 5px;
      font-size: 14px;
    }
    label{
      float: left;
      width: 50%;
      font-weight: 600;
    }
    input{
      width: initial;
      display: inline-block;
      margin: 0;
    }
    .clear{ clear:both; }
  }

  & .message {
    padding: 15px;
    font-weight: 700;
    color: #555;
  }

  & .sub-message{
    font-size: 14px;
    padding: 0 15px 15px;
    font-weight: 700;
    color: #777;

    & strong {
      color: #555;
      text-decoration: underline;
    }
  }

  & .controls{
    padding: 0px 15px 15px;

    & .padding-R { padding-right: 5px; }
    & .padding-L { padding-left: 5px; }
  }

  & .releasing {
    & .message { padding-top: 10px; }
    & i {
      margin-top: 18px;
      color: #0D79F2;

      &.success { color: #26D97A; }
    }
    .button{ margin-top:10px; }
  }
}

.fixed-bottom {
  position: fixed;
  bottom: 0;
  left: 0;
  width: $left-panel-width;
  height: $fixed-bottom-height;
  z-index: 3;
  background: #eee;
  padding: 10px 15px 10px 10px;
  display: flex;
  box-shadow: 0 -1px 2px rgba(0,0,0, .25);
  justify-content: center;
}

.refresh-success{
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  text-align: center;
  font-size: 15px;
  line-height: 60px;
  font-weight: 600;
  color: #2ed376;
  background: #fff;
  z-index: 2;
}

.prov-options{
  display: flex;
  align-items: center;
  margin-left: 5px;
  cursor: pointer;
  color: #0D79F2;
  transition: all .4s ease;
  position: relative;
  z-index: 5;

  .fa-ellipsis-v{ font-size: 18px; }
  &:hover{ color: #333; }
  .options{
    transform-origin: top center;
    position: absolute;
    top: 120%;
    width: 200px;
    background: #fff;
    padding: 10px;
    border-radius: 4px;
    box-shadow: 1px 1px 4px rgba(0,0,0, .2);

    .button {
      margin-bottom: 3px;

      i {
        margin-right: 5px;
      }
    }
  }
}

.slide-up-enter-active, .slide-up-leave-active { transition: all .3s ease; }
.slide-up-enter, .slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

@media only screen and (max-width: 430px) {
  .map-here {
    width: 100%;
    z-index: 5;

    &#mapHere {
      left: 0;
      width: 100%;
      top: 100%;
      transition: all 0.3s ease;
    }

    &.showMapBox {
      &#mapHere {
        top: 0;
      }
    }
  }

  .heading .edit {
    float: none;
    margin: 0;
  }

  .package-container {
    width: 100%;
    padding-bottom: 85px;
    .edit-btn-div {
      padding-left: 13px;
    }
    .tab {
      margin-top: 50px;
      display: table;

      li {
        padding: 12px 10px;
        vertical-align: middle;
        display: table-cell;
        font-size: 12px
      }
    }

  }
  .toggle-mobile {
    display: block;
    color: #fff;
    background-color: #333;
    cursor: pointer;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    text-align: center;
    padding: 8px 0;
    height: 45px;
    -webkit-transition: top .25s;
    transition: top .25s;
    z-index: 9;
    text-transform: uppercase;
    font-weight: 900;
  }

  .editing {
    .left-panel,
    .fixed-bottom {
      width: 100%;
    }

    .tab {
      li {
        padding: 5px 13px;
      }
    }

  }
  .tab-contents {
    .tab-content {
      padding: 10px;
    }
  }

  .transaction-tab {
    width: max-content;
  }

  .select-user {
    width: 95vw !important;
    height: auto;
  }

  .right-panel {
    width: calc(100vw - 55px) !important;
  }

  .button.small {
    font-size: 11px;
    width: auto;
    padding: 7px;
  }

  .fixed-bottom {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100vw;
    bottom: 45px;
    .button.small {
      font-size: 10px;
      width: auto;
      padding: 7px 15px;
    }
    .create {
      font-size: 14px !important;
      padding: 10px 22px !important;
    }
  }

  .modal .modal-content {
    width: 100%;
  }
}
</style>
</style>



// WEBPACK FOOTER //
// src/components/PackageDetails/index2.vue